/**
 * FleetManager V3 - Activity Widget
 * Recent activity timeline - clickable items navigate to relevant views
 *
 * i18n : un item porte `actionKey` + `actionParams` (résolus par t()) et
 * `timestamp` (rendu par fmt.relative() dans la langue active). La cible de
 * navigation vient de `item.target` — plus de détection par mots français.
 * Compat : un item qui porte encore `action` / `time` en clair est affiché
 * tel quel, et sa cible déduite de `item.type` si présent.
 */

import React from 'react';
import { Clock, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '../../shared/index.js';
import { useFormat } from '../../../../i18n/format.js';

// Map legacy activity types to navigation targets
const ACTIVITY_NAVIGATION = {
  vehicle_updated: 'vehicles',
  vehicle_created: 'vehicles',
  claim_declared: 'claims',
  claim_updated: 'claims',
  maintenance_scheduled: 'maintenance',
  maintenance_completed: 'maintenance',
  driver_updated: 'drivers',
  driver_created: 'drivers',
  inspection_completed: 'inspections',
  document_uploaded: 'documents',
  fuel_log: 'fuel'
};

function ActivityItem({ item, isLast, onNavigate }) {
  const { t, fmt } = useFormat();
  const targetView = item.target || ACTIVITY_NAVIGATION[item.type] || null;
  const label = item.actionKey ? t(item.actionKey, item.actionParams) : (item.action ?? '');
  const when = item.timestamp ? fmt.relative(item.timestamp) : (item.time ?? '');

  const handleClick = () => {
    if (targetView) {
      onNavigate?.(targetView, { activityId: item.id });
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`flex items-start gap-2 ${targetView ? 'cursor-pointer' : ''} group`}
    >
      <div className="relative flex flex-col items-center">
        <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5" />
        {!isLast && (
          <div className="w-px h-full bg-gray-300 dark:bg-slate-700 absolute top-3" />
        )}
      </div>
      <div className={`pb-3 flex-1 ${targetView ? 'hover:bg-gray-100 dark:hover:bg-slate-700/20 -mx-2 px-2 rounded-lg transition-colors' : ''}`}>
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-900 dark:text-white">{label}</div>
          {targetView && (
            <ChevronRight size={12} className="text-gray-400 dark:text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </div>
        <div className="text-xs text-gray-500 dark:text-slate-500">{when}</div>
      </div>
    </div>
  );
}

export function ActivityWidget({ activities = [], onNavigate }) {
  const { t } = useFormat();
  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Clock} iconColor="text-slate-400">{t('dashboard.activity.title')}</CardTitle>
      </CardHeader>

      {activities.length > 0 ? (
        <div>
          {activities.slice(0, 5).map((item, index) => (
            <ActivityItem
              key={item.id}
              item={item}
              isLast={index === Math.min(activities.length - 1, 4)}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-4">
          <div className="text-sm text-gray-500 dark:text-slate-400">{t('dashboard.activity.empty')}</div>
        </div>
      )}
    </Card>
  );
}

export default ActivityWidget;
